import { ActionButton, FormInputRow } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { KeyboardArrowRight } from '@mui/icons-material'
import { Autocomplete, Box, Checkbox, Collapse, Divider, FormControl, FormHelperText, Grid, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Switch, TextField, Typography, useTheme } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import timezones from 'timezones-list'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useFeatureFormContext } from './feature-form-context'
import { useStyles } from './style'

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
]

const REPORT_OPTIONS = [
  { value: 'full', label: 'Full Report' },
  { value: 'usage', label: 'Usage' },
  { value: 'content', label: 'Content' },
  { value: 'skillbites', label: 'Skill Bites' }
]

const DEFAULT_REPORT_OPTIONS = REPORT_OPTIONS.reduce((acc, { value }) => {
  acc[value] = true
  return acc
}, {})

const MenuSwitchInput = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      timeZone: tenant.features?.reportEmail?.timeZone || 'Europe/London',
      frequency: tenant.features?.reportEmail?.frequency || 'monthly',
      emailRecipient: tenant.features?.reportEmail?.emailRecipient || '',
      reports: tenant.features?.reportEmail?.reports || DEFAULT_REPORT_OPTIONS
    }
  })
  const { getValues, setValue, formState: { errors, isValid } } = form

  const [isLoading, setIsLoading] = useState(false)
  const [checked, setChecked] = useState(!!tenant.features?.reportEmail?.enabled)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const timezoneOptions = useMemo(() => timezones.map((tz) => ({ label: `(GMT${tz.utc}) ${tz.tzCode}`, id: tz.tzCode })), [])

  const handleSwitch = () =>
    setChecked((checked) => {
      if (!checked && !tenant.features?.reportEmail) {
        // Set default values
        setValue('timeZone', 'Europe/London')
        setValue('frequency', 'monthly')
        setValue('emailRecipient', '')
        setValue('reports', DEFAULT_REPORT_OPTIONS)
      }

      if (checked) setIsCollapsed(false)
      return !checked
    })
  const handleClickListItem = () => setIsCollapsed((isCollapsed) => !isCollapsed)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Email report scheduler settings updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error saving the toolkit features configuration', isError: true })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        reportEmail: {
          ...getValues(),
          enabled: checked
        }
      }
    })
    setIsLoading(true)
  }

  const isSaveDisabled = useMemo(() => isLoading || (!isValid && checked), [isLoading, isValid, checked])

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box border={1} borderColor='#E0E0E0' style={{ padding: spacing(1, 4) }}>
          <Box display='flex' style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              Email Report Scheduler
            </Typography>
            <Box
              display='flex'
              style={{ justifyContent: 'flex-end', alignItems: 'flex-start', padding: spacing(1) }}
            >
              <Box visibility={checked ? 'visible' : 'hidden'}>
                <List component='nav' aria-label='Device settings'>
                  <ListItem
                    button
                    aria-haspopup='true'
                    aria-controls='lock-menu'
                    aria-label='when device is locked'
                    onClick={handleClickListItem}
                  >
                    <ListItemText
                      disableTypography
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Typography variant='body2'>Settings</Typography>
                      <KeyboardArrowRight />
                    </ListItemText>
                  </ListItem>
                </List>
              </Box>
              {checked && (
                <Divider
                  orientation='vertical'
                  flexItem
                  style={{
                    margin: spacing(3, 0),
                    borderColor: 'rgba(0, 0, 0, 0.38)'
                  }}
                />
              )}
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Switch
                  color='primary'
                  checked={checked}
                  onChange={handleSwitch}
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </Box>
          <Collapse in={isCollapsed}>
            <Box className={classes.formCtn}>
              <Grid item xs={12}>
                <FormInputRow
                  title='Time Zone'
                  description='Set the client’s time zone.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='timeZone'
                      control={form.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl
                          sx={{ width: '100%' }}
                          error={errors.timeZone !== undefined}
                          variant='outlined'
                          className={classes.selectControl}
                        >
                          <Autocomplete
                            disablePortal
                            options={timezoneOptions}
                            isOptionEqualToValue={(option, value) => option.id === value}
                            {...field}
                            onChange={(_, value) => {
                              field.onChange(value?.id)
                            }}
                            renderInput={(params) => <TextField {...params} label='Time Zone' />}
                          />
                          {errors.timeZone !== undefined && (
                            <FormHelperText>Time Zone is required</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Frequency'
                  description='Choose the frequency for sending reports to the client’s mailbox.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='frequency'
                      control={form.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl
                          sx={{ width: '100%' }}
                          error={errors.frequency !== undefined}
                          variant='outlined'
                          className={classes.selectControl}
                        >
                          <InputLabel id='frequency-select-lbl'>Frequency</InputLabel>
                          <Select
                            variant='outlined'
                            {...field}
                            id='frequency-select'
                            labelId='frequency-select-lbl'
                            value={field.value || ''}
                          >
                            {FREQUENCY_OPTIONS.map((opt) => (
                              <MenuItem value={opt.value} key={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.frequency !== undefined && (
                            <FormHelperText>Frequency is required</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Email Recipient'
                  description='Specify the mailbox that will receive the reports.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='emailRecipient'
                      control={form.control}
                      rules={{
                        required: 'Email Recipient is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email address'
                        }
                      }}
                      render={({ field }) => (
                        <FormControl
                          sx={{ width: '100%' }}
                          error={errors.emailRecipient !== undefined}
                          variant='outlined'
                          className={classes.selectControl}
                        >
                          <TextField
                            {...field}
                            label='Email Recipient'
                            variant='outlined'
                            error={errors.emailRecipient !== undefined}
                            helperText={errors.emailRecipient?.message}
                          />
                        </FormControl>
                      )}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Reports'
                  description='Select the reports the client must receive on their specified mailbox.'
                  smallFormTitles
                  InputComponent={
                    <Controller
                      name='reports'
                      control={form.control}
                      render={({ field }) => (
                        <FormControl component='fieldset' error={errors.reports !== undefined}>
                          <Box>
                            {REPORT_OPTIONS.map(({ label, value }) => (
                              <Box key={value}>
                                <Checkbox
                                  value={value}
                                  checked={field.value?.[value] || false}
                                  onChange={(e) => {
                                    const fieldValue = { ...field.value }
                                    fieldValue[value] = e.target.checked
                                    field.onChange(fieldValue)
                                  }}
                                />
                                {label}
                              </Box>
                            ))}
                          </Box>
                          {errors.reports !== undefined && (
                            <FormHelperText>At least one report must be selected</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  }
                />
              </Grid>
            </Box>
          </Collapse>
          <Divider style={{ marginBottom: spacing(1) }} />
          <Box className={classes.ctnControls} display='flex' justifyContent='flex-end'>
            <ActionButton onClick={handleSave} disabled={isSaveDisabled} variant='primary'>
              {' '}
              Save
            </ActionButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default MenuSwitchInput
